import { useQuery } from "@apollo/client";
import {
  formatDateRange,
  hasActiveDateFilter,
  hasDateFilter,
  removeDateFilter,
  toggleDateFilter,
  formatWeightRange,
  hasWeightFilter,
  removeWeightFilter,
  toggleWeightFilter,
} from "./utils";
import { supplyChainLotBreedsQuery, getBreedOptions } from "@/benchmarks/BenchmarkFilters/BreedsPicker";
import {
  destinationCitiesQuery,
  getCityOptions as getDestinationCityOptions,
} from "@/benchmarks/BenchmarkFilters/DestinationCityPicker";
import { getDestinationOptions, lotDestinationsQuery } from "@/benchmarks/BenchmarkFilters/DestinationPicker";
import { destinationStateCodesQuery } from "@/benchmarks/BenchmarkFilters/DestinationStatePicker";
import { getFeedyardsOptions, supplyChainLotFeedyardsQuery } from "@/benchmarks/BenchmarkFilters/FeedyardPicker";
import { sexOptions } from "@/benchmarks/BenchmarkFilters/SexPicker";
import {
  sourceCitiesQuery,
  getCityOptions as getSourceCityOptions,
} from "@/benchmarks/BenchmarkFilters/SourceCityPicker";
import { getSourcesOptions, lotSourcesQuery } from "@/benchmarks/BenchmarkFilters/SourcePicker";
import { sourceStateCodesQuery } from "@/benchmarks/BenchmarkFilters/SourceStatePicker";
import { FilterBadge, FilterBadgeGroup, type FilterBadgeItem } from "@/components/FilterBadges";
import { getFilterBadgeItems, toggleFilterState } from "@/components/FilterBadges/utils";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { getSubdivisionOptionsForCodes } from "@/lib/utils/countries";
import { type BenchmarkFilters } from "@/routes/(authed)/_layout.benchmarks";

export type FilterBadgesProps = {
  filters: BenchmarkFilters;
  setFilters: React.Dispatch<React.SetStateAction<BenchmarkFilters>>;
  inactiveFilters: BenchmarkFilters;
  setInactiveFilters: React.Dispatch<React.SetStateAction<BenchmarkFilters>>;
  setSelectedBenchmarkName: React.Dispatch<React.SetStateAction<string>>;
};

type FilterKey = keyof Pick<
  BenchmarkFilters,
  | "sexClass"
  | "animalBreedIds"
  | "lotDestinationIds"
  | "lotSourceIds"
  | "feedyardIds"
  | "sourceStateCodes"
  | "destinationStateCodes"
  | "sourceCities"
  | "destinationCities"
>;

export function FilterBadges({
  filters,
  setFilters,
  inactiveFilters,
  setInactiveFilters,
  setSelectedBenchmarkName,
}: FilterBadgesProps) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { data: breedsData, loading: loadingBreeds } = useQuery(supplyChainLotBreedsQuery);
  const { data: destinationsData, loading: loadingDestinations } = useQuery(lotDestinationsQuery);
  const { data: sourcesData, loading: loadingSources } = useQuery(lotSourcesQuery);
  const { data: feedyardsData, loading: loadingFeedyards } = useQuery(supplyChainLotFeedyardsQuery, {
    variables: { businessUnit: activeBusinessUnitId || "" },
    skip: !activeBusinessUnitId,
  });
  const { data: sourceStatesData, loading: loadingSourceStates } = useQuery(sourceStateCodesQuery);
  const { data: destinationStatesData, loading: loadingDestinationStates } = useQuery(destinationStateCodesQuery);
  const { data: sourceCitiesData, loading: loadingSourceCities } = useQuery(sourceCitiesQuery, {
    variables: { businessUnitId: activeBusinessUnitId! },
    skip: !activeBusinessUnitId,
  });
  const { data: destinationCitiesData, loading: loadingDestinationCities } = useQuery(destinationCitiesQuery, {
    variables: { businessUnitId: activeBusinessUnitId! },
    skip: !activeBusinessUnitId,
  });

  const loading =
    loadingBreeds ||
    loadingDestinations ||
    loadingSources ||
    loadingFeedyards ||
    loadingSourceStates ||
    loadingDestinationStates ||
    loadingSourceCities ||
    loadingDestinationCities;

  const toggleFilter = (key: FilterKey, value: string) => {
    const result = toggleFilterState({
      value,
      activeFilters: filters[key],
      inactiveFilters: inactiveFilters[key],
    });

    setFilters({
      ...filters,
      [key]: result.activeFilters,
    });
    setInactiveFilters({
      ...inactiveFilters,
      [key]: result.inactiveFilters,
    });
  };

  const removeFilter = (key: FilterKey, value: string) => {
    setSelectedBenchmarkName("");

    setFilters({
      ...filters,
      [key]: filters[key]?.filter((v) => v !== value),
    });
    setInactiveFilters({
      ...inactiveFilters,
      [key]: inactiveFilters[key]?.filter((v) => v !== value),
    });
  };

  const allSexFilters = getFilterBadgeItems({
    options: sexOptions,
    activeFilters: filters.sexClass,
    inactiveFilters: inactiveFilters.sexClass,
  });

  const allFeedyardFilters = getFilterBadgeItems({
    options: getFeedyardsOptions(feedyardsData),
    activeFilters: filters.feedyardIds,
    inactiveFilters: inactiveFilters.feedyardIds,
  });

  const allBreedFilters = getFilterBadgeItems({
    options: getBreedOptions(breedsData),
    activeFilters: filters.animalBreedIds,
    inactiveFilters: inactiveFilters.animalBreedIds,
  });

  const destinationOptions = getDestinationOptions(destinationsData);
  const allDestinationsOptions = getFilterBadgeItems({
    options: destinationOptions,
    activeFilters: filters.lotDestinationIds,
    inactiveFilters: inactiveFilters.lotDestinationIds,
  });

  const sourceOptions = getSourcesOptions(sourcesData);
  const allSourcesOptions = getFilterBadgeItems({
    options: sourceOptions,
    activeFilters: filters.lotSourceIds,
    inactiveFilters: inactiveFilters.lotSourceIds,
  });

  const sourceStateOptions = getSubdivisionOptionsForCodes(
    removeNothings(sourceStatesData?.supplyChainLotSourceStateCodes || []),
  );
  const allSourceStateOptions = getFilterBadgeItems({
    options: sourceStateOptions.map((option) => ({
      ...option,
      label: `From: ${option.label}`,
    })),
    activeFilters: filters.sourceStateCodes,
    inactiveFilters: inactiveFilters.sourceStateCodes,
  });

  const destinationStateOptions = getSubdivisionOptionsForCodes(
    removeNothings(destinationStatesData?.supplyChainLotDestinationStateCodes || []),
  );
  const allDestinationStateOptions = getFilterBadgeItems({
    options: destinationStateOptions.map((option) => ({
      ...option,
      label: `To: ${option.label}`,
    })),
    activeFilters: filters.destinationStateCodes,
    inactiveFilters: inactiveFilters.destinationStateCodes,
  });

  const sourceCityNodes = removeNothings(
    sourceCitiesData?.supplyChainLotSourceCities?.edges?.map((edge) => edge?.node) ?? [],
  );
  const sourceCityOptions = getSourceCityOptions(sourceCityNodes);
  const allSourceCityOptions = getFilterBadgeItems({
    options: sourceCityOptions.map((option) => ({
      ...option,
      label: `From: ${option.label}`,
    })),
    activeFilters: filters.sourceCities,
    inactiveFilters: inactiveFilters.sourceCities,
  });

  const destinationCityNodes = removeNothings(
    destinationCitiesData?.supplyChainLotDestinationCities?.edges?.map((edge) => edge?.node) ?? [],
  );
  const destinationCityOptions = getDestinationCityOptions(destinationCityNodes);
  const allDestinationCityOptions = getFilterBadgeItems({
    options: destinationCityOptions.map((option) => ({
      ...option,
      label: `To: ${option.label}`,
    })),
    activeFilters: filters.destinationCities,
    inactiveFilters: inactiveFilters.destinationCities,
  });

  if (loading) {
    return null;
  }

  const removeAllInGroup = (filterKey: FilterKey, items: readonly FilterBadgeItem[]) => {
    setSelectedBenchmarkName("");
    const itemValues = items.map((item) => item.value);

    setFilters((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey]?.filter((v) => !itemValues.includes(v)),
    }));

    setInactiveFilters((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey]?.filter((v) => !itemValues.includes(v)),
    }));
  };

  const renderFilterGroup = (items: readonly FilterBadgeItem[], filterKey: FilterKey, groupLabel: string) => (
    <FilterBadgeGroup
      key={filterKey}
      groupLabel={groupLabel}
      items={items}
      onRemove={(value) => removeFilter(filterKey, value)}
      onToggle={(value) => toggleFilter(filterKey, value)}
      onRemoveAll={() => removeAllInGroup(filterKey, items)}
    />
  );

  return (
    <div data-testid="filter-badges-container" className="flex flex-wrap gap-1">
      {renderFilterGroup(allFeedyardFilters, "feedyardIds", "Feedyard")}
      {renderFilterGroup(allSexFilters, "sexClass", "Sex")}
      {renderFilterGroup(allBreedFilters, "animalBreedIds", "Breeds")}
      {renderFilterGroup(allDestinationsOptions, "lotDestinationIds", "Destination")}
      {renderFilterGroup(allSourcesOptions, "lotSourceIds", "Source")}
      {renderFilterGroup(allSourceStateOptions, "sourceStateCodes", "Source State")}
      {renderFilterGroup(allDestinationStateOptions, "destinationStateCodes", "Destination State")}
      {renderFilterGroup(allSourceCityOptions, "sourceCities", "Source City")}
      {renderFilterGroup(allDestinationCityOptions, "destinationCities", "Destination City")}
      {hasDateFilter(filters, inactiveFilters, "Opened") && (
        <FilterBadge
          label={formatDateRange(filters, inactiveFilters, "Opened")}
          onRemove={() => {
            setSelectedBenchmarkName("");
            const result = removeDateFilter(filters, inactiveFilters, "Opened");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
          active={hasActiveDateFilter(filters, "Opened")}
          onToggle={() => {
            const result = toggleDateFilter(filters, inactiveFilters, "Opened");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
        />
      )}
      {hasDateFilter(filters, inactiveFilters, "Closed") && (
        <FilterBadge
          label={formatDateRange(filters, inactiveFilters, "Closed")}
          onRemove={() => {
            setSelectedBenchmarkName("");
            const result = removeDateFilter(filters, inactiveFilters, "Closed");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
          active={hasActiveDateFilter(filters, "Closed")}
          onToggle={() => {
            const result = toggleDateFilter(filters, inactiveFilters, "Closed");
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
        />
      )}
      {hasWeightFilter(filters, inactiveFilters) ? (
        <FilterBadge
          label={formatWeightRange(
            filters.weightAverageInMin || inactiveFilters.weightAverageInMin,
            filters.weightAverageInMax || inactiveFilters.weightAverageInMax,
          )}
          onRemove={() => {
            setSelectedBenchmarkName("");
            const result = removeWeightFilter(filters, inactiveFilters);
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
          active={Boolean(filters.weightAverageInMin || filters.weightAverageInMax)}
          onToggle={() => {
            const result = toggleWeightFilter(filters, inactiveFilters);
            setFilters(result.filters);
            setInactiveFilters(result.inactiveFilters);
          }}
        />
      ) : null}
    </div>
  );
}
